// RECRUITER CALL — POST /v3/recruiter-calls/history/{userSlug}
// Body: { jobSlug?: string }
// ***************************************************************************
import useSWR, { SWRResponse } from 'swr';
import { baseURL } from '@utils/data-fetching/utils';
import type { RecruiterCallHistoryList } from '@type/recruiter-call';

interface UseSwrRecruiterCallHistoryProps {
  candidateUserSlug?: string | null;
  jobSlug?: string | null;
  shouldFetch?: boolean;
}

/**
 * @description SWR: fetch call history between the logged-in recruiter's
 * account and the given candidate. POST because the request carries an
 * optional `jobSlug` in the body (kept in the body rather than the query
 * string per the endpoint spec). Cache key is (candidateUserSlug, jobSlug)
 * so switching CV pages / job contexts re-fetches automatically.
 */
export const useSwrRecruiterCallHistory = (
  props: UseSwrRecruiterCallHistoryProps,
): SWRResponse<RecruiterCallHistoryList> => {
  const { candidateUserSlug, jobSlug, shouldFetch = true } = props;

  const canFetch = shouldFetch && Boolean(candidateUserSlug);

  return useSWR<RecruiterCallHistoryList>(
    canFetch ? ['recruiter-call-history', candidateUserSlug, jobSlug ?? null] : null,
    async ([, slug, jsl]: [string, string, string | null]) => {
      const url = `${baseURL}/v3/recruiter-calls/history/${encodeURIComponent(slug)}`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jobSlug: jsl ?? null }),
      });
      if (!response.ok) {
        throw new Error(`RecruiterCall history: HTTP ${response.status}`);
      }
      return (await response.json()) as RecruiterCallHistoryList;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 30 * 1000,
    },
  );
};
